import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import {
  AuditCase,
  AuditCorrectiveAction,
  AuditCorrectiveActionAnalytics,
  AuditCorrectiveActionEscalationRule,
  AuditFinding,
  SelectOption
} from '../audit.models';
import { AuditService } from '../audit.service';
import {
  actionPriorityOptions,
  actionStatusOptions,
  actionTypeOptions,
  auditNavItems,
  auditDaysOverdue,
  booleanFilterOptions,
  escalationLevelOptions,
  escalationStatusOptions,
  formatAuditDate,
  findingTypeOptions,
  isAuditOverdue,
  statusSeverity
} from '../audit-ui';

@Component({
  selector: 'app-audit-actions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  template: `
    <p-toast></p-toast>
    @if (loading) {
      <p-progressBar mode="indeterminate"></p-progressBar>
    }

    <app-page-shell>
      <app-page-hero eyebrow="CAPA" title="Corrective Actions" subtitle="Owner assignments, due dates, action evidence, and reviewer decisions.">
        <div nav>
          @for (item of visibleNavItems; track item.route) {
            <a pButton [routerLink]="item.route" [label]="item.label" [icon]="item.icon" [outlined]="item.route !== '/audit/actions'" severity="secondary"></a>
          }
        </div>
        <div actions>
          <p-button label="Escalation Rules" icon="pi pi-bell" [outlined]="true" severity="secondary" (onClick)="openRules()" [disabled]="!canManage"></p-button>
          <p-button label="Refresh" icon="pi pi-refresh" [outlined]="true" severity="secondary" (onClick)="load()"></p-button>
          <p-button label="New Action" icon="pi pi-plus" (onClick)="openAction()" [disabled]="!canManage"></p-button>
        </div>
      </app-page-hero>

      <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

      <app-page-data-panel title="Action Board" subtitle="Filter by status, priority, finding, case, and aging." [resultText]="filteredActions.length + ' actions'">
        <div class="audit-filter-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-info-circle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-status-filter" [options]="statusFilterOptions" [(ngModel)]="statusFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-status-filter">Status</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-priority-filter" [options]="priorityFilterOptions" [(ngModel)]="priorityFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-priority-filter">Priority</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-clock"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-aging-filter" [options]="agingFilterOptions" [(ngModel)]="agingFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-aging-filter">Aging</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-wrench"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-type-filter" [options]="actionTypeFilterOptions" [(ngModel)]="actionTypeFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-type-filter">Type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-eye-slash"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-weak-filter" [options]="weakEvidenceFilterOptions" [(ngModel)]="evidenceWeakFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-weak-filter">Weak Evidence</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-bell"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-escalation-filter" [options]="escalationStatusFilterOptions" [(ngModel)]="escalationStatusFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-escalation-filter">Escalation</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-exclamation-triangle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-finding-filter" [options]="findingFilterOptions" [(ngModel)]="findingFilter" optionLabel="label" optionValue="value" [filter]="true"></p-select>
              <label for="audit-action-finding-filter">Finding</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-briefcase"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-case-filter" [options]="caseFilterOptions" [(ngModel)]="caseFilter" optionLabel="label" optionValue="value" [filter]="true"></p-select>
              <label for="audit-action-case-filter">Case</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputnumber inputId="audit-action-owner-filter" [(ngModel)]="ownerFilter" [useGrouping]="false"></p-inputnumber>
              <label for="audit-action-owner-filter">Owner</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-sitemap"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="audit-action-module-filter" [options]="moduleFilterOptions" [(ngModel)]="moduleFilter" optionLabel="label" optionValue="value"></p-select>
              <label for="audit-action-module-filter">Module</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-database"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="audit-action-source-filter" [(ngModel)]="sourceFilter">
              <label for="audit-action-source-filter">Source</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="audit-action-root-filter" [(ngModel)]="rootCauseFilter">
              <label for="audit-action-root-filter">Root Cause</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>

        <p-table [value]="filteredActions" [loading]="loading" styleClass="p-datatable-sm" responsiveLayout="scroll" [tableStyle]="{ 'min-width': '1220px' }">
          <ng-template pTemplate="header">
            <tr>
              <th>Action</th>
              <th>Finding</th>
              <th>Case</th>
              <th>Priority</th>
              <th>Status</th>
              <th>Escalation</th>
              <th>Root Cause</th>
              <th>Owner</th>
              <th>Due</th>
              <th class="text-right">Actions</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-action>
            <tr>
              <td>
                <div class="audit-stack">
                  <strong>{{ action.actionNumber || 'Action' }}</strong>
                  <small>{{ action.actionType || '-' }}</small>
                </div>
              </td>
              <td>
                <a [routerLink]="['/audit/findings', action.findingId]">{{ findingLabel(action.findingId) }}</a>
              </td>
              <td>
                <a [routerLink]="['/audit/cases', action.auditCaseId]">{{ caseLabel(action.auditCaseId) }}</a>
              </td>
              <td><p-tag [value]="action.priority || '-'" [severity]="statusSeverity(action.priority)"></p-tag></td>
              <td><p-tag [value]="action.status || '-'" [severity]="statusSeverity(action.status)"></p-tag></td>
              <td>
                <p-tag [value]="action.escalationStatus || 'OPEN'" [severity]="statusSeverity(action.escalationStatus)"></p-tag>
                @if (action.evidenceWeak) {
                  <p-tag value="Weak Evidence" severity="danger"></p-tag>
                }
              </td>
              <td>{{ action.rootCause || '-' }}</td>
              <td>{{ action.ownerUserId || '-' }}</td>
              <td>
                <div class="audit-stack">
                  <span>{{ formatDate(action.dueAt) }}</span>
                  @if (isOverdue(action)) {
                    <small class="audit-danger">{{ daysOverdue(action) }} days overdue</small>
                  }
                </div>
              </td>
              <td class="text-right">
                <a pButton [routerLink]="['/audit/actions', action.actionId]" icon="pi pi-arrow-right" [text]="true" pTooltip="Open action"></a>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="10" class="text-center p-4">No corrective actions match the current filters.</td></tr>
          </ng-template>
        </p-table>
      </app-page-data-panel>
    </app-page-shell>

    <p-dialog header="New Corrective Action" [(visible)]="dialogVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(760px, 96vw)' }">
      <div class="audit-form-grid">
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-exclamation-triangle"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="new-action-finding" [options]="findingOptions" [(ngModel)]="selectedFindingId" optionLabel="label" optionValue="value" [filter]="true"></p-select>
            <label for="new-action-finding">Finding</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-wrench"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="new-action-type" [options]="actionTypeOptions" [(ngModel)]="form.actionType" optionLabel="label" optionValue="value"></p-select>
            <label for="new-action-type">Type</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-select inputId="new-action-priority" [options]="priorityOptions" [(ngModel)]="form.priority" optionLabel="label" optionValue="value"></p-select>
            <label for="new-action-priority">Priority</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-user"></i></p-inputgroup-addon>
          <p-iftalabel>
            <p-inputNumber inputId="new-action-owner" [(ngModel)]="form.ownerUserId" [useGrouping]="false"></p-inputNumber>
            <label for="new-action-owner">Owner User ID</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup>
          <p-inputgroup-addon><i class="pi pi-calendar-clock"></i></p-inputgroup-addon>
          <p-iftalabel>
            <input pInputText id="new-action-due-at" type="datetime-local" [(ngModel)]="dueAtInput">
            <label for="new-action-due-at">Due Date</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-align-left"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="new-action-description" rows="4" [(ngModel)]="form.description"></textarea>
            <label for="new-action-description">Description</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-search"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="new-action-root-cause" rows="3" [(ngModel)]="form.rootCause"></textarea>
            <label for="new-action-root-cause">Root Cause</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="new-action-preventive" rows="3" [(ngModel)]="form.preventiveAction"></textarea>
            <label for="new-action-preventive">Preventive Action</label>
          </p-iftalabel>
        </p-inputgroup>
        <p-inputgroup class="wide-field">
          <p-inputgroup-addon><i class="pi pi-refresh"></i></p-inputgroup-addon>
          <p-iftalabel>
            <textarea pTextarea id="new-action-recurrence" rows="3" [(ngModel)]="form.recurrencePreventionPlan"></textarea>
            <label for="new-action-recurrence">Recurrence Prevention Plan</label>
          </p-iftalabel>
        </p-inputgroup>
      </div>
      @if (formError) {
        <div class="audit-error">{{ formError }}</div>
      }
      <ng-template pTemplate="footer">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (onClick)="dialogVisible = false"></p-button>
        <p-button label="Create Action" icon="pi pi-check" (onClick)="saveAction()" [loading]="saving" [disabled]="!canManage"></p-button>
      </ng-template>
    </p-dialog>

    <p-dialog header="CAPA Escalation Rules" [(visible)]="rulesVisible" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(1040px, 96vw)' }">
      <div class="rule-layout">
        <p-table [value]="escalationRules" styleClass="p-datatable-sm" responsiveLayout="scroll">
          <ng-template pTemplate="header">
            <tr><th>Rule</th><th>Match</th><th>Due</th><th>Escalation</th><th></th></tr>
          </ng-template>
          <ng-template pTemplate="body" let-rule>
            <tr>
              <td><strong>{{ rule.ruleCode }}</strong><br><small>{{ rule.ruleName }}</small></td>
              <td>{{ rule.actionType || 'Any type' }} / {{ rule.priority || 'Any priority' }} / {{ rule.findingType || 'Any finding' }}</td>
              <td>{{ rule.dueDays || '-' }} days<br><small>Effectiveness: {{ rule.effectivenessCheckDays || '-' }} days</small></td>
              <td>{{ rule.escalationLevel || '-' }} after {{ rule.escalationAfterDays || '-' }} days</td>
              <td class="text-right"><p-button icon="pi pi-pencil" [text]="true" (onClick)="editRule(rule)"></p-button></td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr><td colspan="5" class="text-center p-4">No CAPA escalation rules are configured.</td></tr>
          </ng-template>
        </p-table>

        <div class="audit-form-grid">
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-hashtag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="action-rule-code" [(ngModel)]="ruleForm.ruleCode">
              <label for="action-rule-code">Rule Code</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-bookmark"></i></p-inputgroup-addon>
            <p-iftalabel>
              <input pInputText id="action-rule-name" [(ngModel)]="ruleForm.ruleName">
              <label for="action-rule-name">Rule Name</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-wrench"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="action-rule-type" [options]="actionTypeFilterOptions" [(ngModel)]="ruleForm.actionType" optionLabel="label" optionValue="value"></p-select>
              <label for="action-rule-type">Action Type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-flag"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="action-rule-priority" [options]="priorityFilterOptions" [(ngModel)]="ruleForm.priority" optionLabel="label" optionValue="value"></p-select>
              <label for="action-rule-priority">Priority</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-exclamation-triangle"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="action-rule-finding-type" [options]="findingTypeFilterOptions" [(ngModel)]="ruleForm.findingType" optionLabel="label" optionValue="value"></p-select>
              <label for="action-rule-finding-type">Finding Type</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-calendar"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputNumber inputId="action-rule-due-days" [(ngModel)]="ruleForm.dueDays" [useGrouping]="false"></p-inputNumber>
              <label for="action-rule-due-days">Due Days</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-shield"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-inputNumber inputId="action-rule-effectiveness" [(ngModel)]="ruleForm.effectivenessCheckDays" [useGrouping]="false"></p-inputNumber>
              <label for="action-rule-effectiveness">Effectiveness Check Days</label>
            </p-iftalabel>
          </p-inputgroup>
          <p-inputgroup>
            <p-inputgroup-addon><i class="pi pi-bell"></i></p-inputgroup-addon>
            <p-iftalabel>
              <p-select inputId="action-rule-level" [options]="escalationLevelOptions" [(ngModel)]="ruleForm.escalationLevel" optionLabel="label" optionValue="value"></p-select>
              <label for="action-rule-level">Escalation Level</label>
            </p-iftalabel>
          </p-inputgroup>
        </div>
        @if (ruleError) {
          <div class="audit-error">{{ ruleError }}</div>
        }
      </div>
      <ng-template pTemplate="footer">
        <p-button label="New Rule" icon="pi pi-plus" severity="secondary" [outlined]="true" (onClick)="newRule()"></p-button>
        <p-button label="Save Rule" icon="pi pi-save" (onClick)="saveRule()" [loading]="saving"></p-button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .audit-filter-grid,
    .audit-form-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(150px, 1fr));
      gap: .85rem;
      margin-bottom: 1rem;
    }

    .audit-form-grid {
      grid-template-columns: repeat(2, minmax(180px, 1fr));
      margin-bottom: 0;
    }

    .rule-layout {
      display: grid;
      gap: 1rem;
    }

    label {
      color: #334155;
      display: grid;
      gap: .35rem;
      min-width: 0;
    }

    label span {
      color: #64748b;
      font-size: .82rem;
      font-weight: 700;
    }

    input,
    textarea,
    p-iftalabel,
    p-inputgroup,
    p-select,
    p-inputNumber {
      width: 100%;
    }

    .wide-field {
      grid-column: 1 / -1;
    }

    .audit-stack {
      display: flex;
      flex-direction: column;
      gap: .25rem;
      min-width: 0;
    }

    .audit-stack small {
      color: #64748b;
      font-size: .78rem;
    }

    .audit-danger {
      color: #b91c1c !important;
      font-weight: 800;
    }

    .audit-error {
      background: #fef2f2;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #991b1b;
      font-weight: 700;
      margin-top: .85rem;
      padding: .75rem;
    }

    @media (max-width: 920px) {
      .audit-filter-grid,
      .audit-form-grid {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class AuditActionsComponent implements OnInit {
  private readonly auditService = inject(AuditService);
  private readonly route = inject(ActivatedRoute);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  loading = false;
  saving = false;
  dialogVisible = false;
  formError = '';
  actions: AuditCorrectiveAction[] = [];
  findings: AuditFinding[] = [];
  cases: AuditCase[] = [];
  analytics?: AuditCorrectiveActionAnalytics;
  escalationRules: AuditCorrectiveActionEscalationRule[] = [];
  form: AuditCorrectiveAction = {};
  ruleForm: AuditCorrectiveActionEscalationRule = {};
  selectedFindingId?: number;
  dueAtInput = '';
  rulesVisible = false;
  ruleError = '';

  statusFilter: string | null = null;
  priorityFilter: string | null = null;
  actionTypeFilter: string | null = null;
  agingFilter = 'ALL';
  findingFilter: number | null = null;
  caseFilter: number | null = null;
  ownerFilter?: number | null;
  moduleFilter: string | null = null;
  sourceFilter = '';
  rootCauseFilter = '';
  evidenceWeakFilter: boolean | null = null;
  escalationStatusFilter: string | null = null;

  readonly visibleNavItems = auditNavItems.filter(item => this.permissionService.hasPermission(item.permission));
  readonly canManage = this.permissionService.hasPermission(app_permissions.audit.actions.manage);
  readonly actionTypeOptions = actionTypeOptions;
  readonly priorityOptions = actionPriorityOptions;
  readonly escalationLevelOptions = escalationLevelOptions;
  readonly statusSeverity = statusSeverity;
  readonly formatDate = formatAuditDate;
  readonly statusFilterOptions: SelectOption<string | null>[] = [{ label: 'All statuses', value: null }, ...actionStatusOptions];
  readonly priorityFilterOptions: SelectOption<string | null>[] = [{ label: 'All priorities', value: null }, ...actionPriorityOptions];
  readonly actionTypeFilterOptions: SelectOption<string | null>[] = [{ label: 'All types', value: null }, ...actionTypeOptions];
  readonly findingTypeFilterOptions: SelectOption<string | null>[] = [{ label: 'All finding types', value: null }, ...findingTypeOptions];
  readonly weakEvidenceFilterOptions = booleanFilterOptions;
  readonly escalationStatusFilterOptions: SelectOption<string | null>[] = [{ label: 'All escalation', value: null }, ...escalationStatusOptions];
  readonly agingFilterOptions: SelectOption<string>[] = [
    { label: 'All actions', value: 'ALL' },
    { label: 'Overdue', value: 'OVERDUE' },
    { label: 'Due soon', value: 'DUE_SOON' }
  ];

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.statusFilter = params.get('status');
    this.priorityFilter = params.get('priority');
    this.actionTypeFilter = params.get('type');
    this.agingFilter = params.get('aging') || 'ALL';
    this.moduleFilter = params.get('module');
    this.sourceFilter = params.get('source') || '';
    this.rootCauseFilter = params.get('rootCause') || '';
    const owner = Number(params.get('owner'));
    this.ownerFilter = Number.isFinite(owner) && owner > 0 ? owner : null;
    const auditCaseId = Number(params.get('case'));
    this.caseFilter = Number.isFinite(auditCaseId) && auditCaseId > 0 ? auditCaseId : null;
    this.load();
  }

  get findingOptions(): SelectOption<number>[] {
    return this.findings.map(item => ({
      label: `${item.findingNumber || 'Finding'} - ${item.title || '-'}`,
      value: item.findingId!
    }));
  }

  get findingFilterOptions(): SelectOption<number | null>[] {
    return [{ label: 'All findings', value: null }, ...this.findingOptions];
  }

  get caseFilterOptions(): SelectOption<number | null>[] {
    return [
      { label: 'All cases', value: null },
      ...this.cases.map(item => ({
        label: item.auditNumber || `Case #${item.auditCaseId}`,
        value: item.auditCaseId!
      }))
    ];
  }

  get moduleFilterOptions(): SelectOption<string | null>[] {
    const modules = new Set<string>();
    for (const action of this.actions) {
      if (action.sourceModule) {
        modules.add(action.sourceModule);
      }
      const auditCase = this.cases.find(item => item.auditCaseId === action.auditCaseId);
      if (auditCase?.moduleCode) {
        modules.add(auditCase.moduleCode);
      }
    }
    return [
      { label: 'All modules', value: null },
      ...Array.from(modules).sort().map(module => ({ label: module, value: module }))
    ];
  }

  get filteredActions(): AuditCorrectiveAction[] {
    return this.actions.filter(action => {
      const statusMatch = !this.statusFilter || action.status === this.statusFilter;
      const priorityMatch = !this.priorityFilter || action.priority === this.priorityFilter;
      const typeMatch = !this.actionTypeFilter || action.actionType === this.actionTypeFilter;
      const findingMatch = !this.findingFilter || action.findingId === this.findingFilter;
      const caseMatch = !this.caseFilter || action.auditCaseId === this.caseFilter;
      const ownerMatch = this.ownerFilter === undefined || this.ownerFilter === null || action.ownerUserId === this.ownerFilter;
      const auditCase = this.cases.find(item => item.auditCaseId === action.auditCaseId);
      const moduleMatch = !this.moduleFilter || action.sourceModule === this.moduleFilter || auditCase?.moduleCode === this.moduleFilter;
      const sourceNeedle = this.sourceFilter.trim().toLowerCase();
      const rootNeedle = this.rootCauseFilter.trim().toLowerCase();
      const sourceMatch = !sourceNeedle || [
        action.sourceModule,
        action.sourceResourceType,
        action.sourceResourceNumber,
        auditCase?.sourceModule,
        auditCase?.sourceResourceType,
        auditCase?.sourceResourceNumber,
        auditCase?.sourceDisplayName
      ].some(value => (value || '').toLowerCase().includes(sourceNeedle));
      const agingMatch = this.agingFilter === 'OVERDUE'
        ? this.isOverdue(action)
        : this.agingFilter === 'DUE_SOON'
          ? this.isDueSoon(action)
          : true;
      const weakMatch = this.evidenceWeakFilter === null || this.evidenceWeakFilter === undefined
        || Boolean(action.evidenceWeak) === this.evidenceWeakFilter;
      const escalationMatch = !this.escalationStatusFilter || action.escalationStatus === this.escalationStatusFilter;
      const rootMatch = !rootNeedle || (action.rootCause || '').toLowerCase().includes(rootNeedle);
      return statusMatch && priorityMatch && typeMatch && findingMatch && caseMatch && ownerMatch && moduleMatch
        && sourceMatch && agingMatch && weakMatch && escalationMatch && rootMatch;
    });
  }

  get metrics(): PageMetric[] {
    return [
      { label: 'Actions', value: this.analytics?.totalActions ?? this.actions.length, tone: 'info' },
      { label: 'Overdue', value: this.analytics?.overdueActions ?? this.actions.filter(action => this.isOverdue(action)).length, tone: 'danger' },
      { label: 'Weak Evidence', value: this.analytics?.weakEvidenceActions ?? this.actions.filter(action => action.evidenceWeak).length, tone: 'danger' },
      { label: 'Closed', value: this.analytics?.closedActions ?? this.actions.filter(action => action.status === 'CLOSED').length, tone: 'success' }
    ];
  }

  load(): void {
    this.loading = true;
    forkJoin({
      actions: this.auditService.actions({
        status: this.statusFilter || undefined,
        priority: this.priorityFilter || undefined,
        actionType: this.actionTypeFilter || undefined,
        ownerUserId: this.ownerFilter || undefined,
        sourceModule: this.moduleFilter || undefined,
        evidenceWeak: this.evidenceWeakFilter,
        escalationStatus: this.escalationStatusFilter || undefined,
        rootCause: this.rootCauseFilter || undefined,
        overdue: this.agingFilter === 'OVERDUE' ? true : null
      }),
      analytics: this.auditService.actionAnalytics(),
      findings: this.auditService.findings(),
      cases: this.auditService.cases()
    }).subscribe({
      next: response => {
        this.actions = response.actions.data ?? [];
        this.analytics = response.analytics.data;
        this.findings = response.findings.data ?? [];
        this.cases = response.cases.data ?? [];
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'Corrective actions', detail: 'Unable to load corrective actions.' }),
      complete: () => this.loading = false
    });
  }

  openAction(): void {
    this.formError = '';
    this.form = { actionType: 'CORRECTIVE', priority: 'MEDIUM', status: 'OPEN' };
    this.selectedFindingId = undefined;
    this.dueAtInput = '';
    this.dialogVisible = true;
  }

  openRules(): void {
    this.rulesVisible = true;
    this.newRule();
    this.loadRules();
  }

  loadRules(): void {
    this.auditService.actionEscalationRules().subscribe({
      next: response => this.escalationRules = response.data ?? [],
      error: () => this.messageService.add({ severity: 'error', summary: 'Escalation rules', detail: 'Unable to load CAPA rules.' })
    });
  }

  newRule(): void {
    this.ruleError = '';
    this.ruleForm = { active: true, escalationLevel: 'L1', dueDays: 7, effectivenessCheckDays: 30 };
  }

  editRule(rule: AuditCorrectiveActionEscalationRule): void {
    this.ruleError = '';
    this.ruleForm = { ...rule };
  }

  saveRule(): void {
    this.ruleError = '';
    if (!this.ruleForm.ruleCode?.trim() || !this.ruleForm.ruleName?.trim()) {
      this.ruleError = 'Rule code and rule name are required.';
      return;
    }
    this.saving = true;
    this.auditService.saveActionEscalationRule(this.ruleForm).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Escalation rule', detail: response.message || 'Rule saved.' });
        this.newRule();
        this.loadRules();
      },
      error: error => this.ruleError = error?.error?.message || 'Unable to save CAPA escalation rule.',
      complete: () => this.saving = false
    });
  }

  saveAction(): void {
    this.formError = '';
    if (!this.selectedFindingId || !this.form.description?.trim()) {
      this.formError = 'Finding and description are required.';
      return;
    }
    this.form.dueAt = this.dueAtInput ? new Date(this.dueAtInput).toISOString() : undefined;
    this.saving = true;
    this.auditService.createFindingAction(this.selectedFindingId, this.form).subscribe({
      next: response => {
        this.messageService.add({ severity: 'success', summary: 'Action created', detail: response.message || 'Corrective action created.' });
        this.dialogVisible = false;
        this.load();
      },
      error: error => this.formError = error?.error?.message || 'Unable to create corrective action.',
      complete: () => this.saving = false
    });
  }

  findingLabel(findingId?: number): string {
    const finding = this.findings.find(item => item.findingId === findingId);
    return finding?.findingNumber || finding?.title || `Finding #${findingId || '-'}`;
  }

  caseLabel(auditCaseId?: number): string {
    const auditCase = this.cases.find(item => item.auditCaseId === auditCaseId);
    return auditCase?.auditNumber || `Case #${auditCaseId || '-'}`;
  }

  isOverdue(action: AuditCorrectiveAction): boolean {
    return Boolean(action.overdue) || isAuditOverdue(action.dueAt, action.status);
  }

  daysOverdue(action: AuditCorrectiveAction): number {
    return action.daysOverdue || auditDaysOverdue(action.dueAt, action.status);
  }

  private isDueSoon(action: AuditCorrectiveAction): boolean {
    if (!action.dueAt || this.isOverdue(action) || ['CLOSED', 'VERIFIED'].includes((action.status || '').toUpperCase())) {
      return false;
    }
    const dueAt = new Date(action.dueAt).getTime();
    return !Number.isNaN(dueAt) && dueAt <= Date.now() + 7 * 86_400_000;
  }
}
